import { Key, useContext, useEffect, useState } from 'react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubChip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import { UserContext } from '../../contexts/UserContext';
import { ConfigHelper } from '@devxdiscover/devhub-common';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate } from 'react-router-dom';

type TaskType = {
  itrcTpeId?: Key;
  stepPhase?: string;
  stepStatus?: string;
  stepLink?: string;
  stepDisplayName: string;
  stepDueDate?: Date;
};

type PrivacyWorkCountProps = {
  privacyData: TaskType[];
};

type PrivacyWorkProps = {
  showCount?: boolean;
};

/**
 * Privacy Work Count component
 * @param privacyData - privacy data
 * @constructor
 */
export const PrivacyWorkCount = ({ privacyData }: PrivacyWorkCountProps) => {
  const privacyTaskCount = privacyData.length;
  const navigate = useNavigate();

  const handleViewAll = (component: string) => {
    navigate(`/action-center/${component}`);
  };

  return (
    <DevHubAlert severity="info">
      <DevHubBox display="flex" alignItems="center">
        <DevHubTypography>
          You have {privacyTaskCount} privacy-related tasks.
        </DevHubTypography>
        <DevHubButton
          buttonColor="themeBased"
          onClick={() => handleViewAll('privacy')}
        >
          <DevHubTypography fontColor="blue" fontSize="small">
            View All
          </DevHubTypography>
        </DevHubButton>
      </DevHubBox>
    </DevHubAlert>
  );
};

/**
 * Privacy Work component
 * @param showCount - show count
 * @constructor
 */
export const PrivacyWork = ({ showCount = false }: PrivacyWorkProps) => {
  const limit = 15;
  const login = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [privacyData, setPrivacyData] = useState<TaskType[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          ConfigHelper.instance.proxyUrl(
            `/privacy/uwl?tsoNtid=${login?.login}&limit=${limit}`,
          ).href,
        );
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const jsonData = await response.json();
        setPrivacyData(jsonData);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchData();
  }, [login, limit]);

  const getChipColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'minimize':
        return 'blue';
      case 'request':
        return 'purple';
      case 'identify':
        return 'yellow';
      case 'success':
        return 'green';
      default:
        return 'medGray';
    }
  };

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  if (showCount && privacyData.length > 0) {
    return <PrivacyWorkCount privacyData={privacyData} />;
  } else if (showCount && privacyData.length === 0) {
    return (
      <DevHubAlert severity="info">
        No privacy-related work available at the moment.
      </DevHubAlert>
    );
  }

  return (
    <>
      {loading && <Progress />}
      {!loading && privacyData.length === 0 && (
        <DevHubAlert severity="info">
          No privacy-related work available at the moment.
        </DevHubAlert>
      )}
      {!loading && privacyData.length > 0 && (
        <>
          <DevHubBox>
            <DataTable
              value={privacyData}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 20]}
            >
              <Column field="itrcTpeId" header="App ID" />
              <Column
                field="stepPhase"
                header="Phase"
                body={rowData => (
                  <DevHubChip
                    label={rowData.stepPhase
                      ?.split(' ')
                      .map(
                        (word: string) =>
                          word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(' ')}
                    noBottomMargin
                    chipSize="medium"
                    chipColor={getChipColor(rowData.stepPhase)}
                  />
                )}
              />
              <Column field="stepDisplayName" header="Step" />
              <Column
                field="stepDueDate"
                header="Due Date"
                body={rowData =>
                  rowData.stepDueDate
                    ? new Date(rowData.stepDueDate).toLocaleDateString()
                    : ''
                }
              />
              <Column
                header="Take Action"
                body={rowData =>
                  rowData.stepLink && (
                    <DevHubButton
                      variant="contained"
                      buttonColor="black"
                      onClick={() => window.open(rowData.stepLink, '_blank')}
                    >
                      Take Action
                    </DevHubButton>
                  )
                }
              />
            </DataTable>
          </DevHubBox>
        </>
      )}
    </>
  );
};
